<template>
    <div ref="costAnalysisDashboard" class="dashboard-container">
        <!-- 첫 진입 시 전체 로딩 화면 -->
        <div v-if="isInitialLoad && isLoading" class="loading-overlay">
            <div class="loading-content">
                <div class="loading-spinner">
                    <div class="spinner-ring"></div>
                    <div class="spinner-ring"></div>
                    <div class="spinner-ring"></div>
                </div>
                <div class="loading-text">데이터를 불러오는 중...</div>
                <div class="loading-subtext">잠시만 기다려주세요</div>
            </div>
        </div>
        <!-- Pending 상태일 때 전체를 덮는 placeholder -->
        <div v-else-if="dataStatus === 'pending' || !dashboardData" class="data-empty-placeholder">
            <div class="empty-content">
                <div class="empty-icon">
                    <i class="fas fa-inbox"></i>
                </div>
                <div class="empty-text">데이터가 없습니다</div>
                <div class="empty-subtext">선택한 기간에 해당하는 데이터가 존재하지 않습니다</div>
            </div>
        </div>
        <div v-else class="dashboard-content">
            <!-- 상단: 요약 정보 (간소화) -->
            <div class="summary-section">
                <div class="summary-card summary-card-primary">
                    <i class="fas fa-coins summary-card-icon"></i>
                    <div class="summary-label">총 청구 금액</div>
                    <div class="summary-value-container">
                        <div class="summary-value">{{ formatCurrency(dashboardData?.totals?.totalAmount) }}</div>

                        <span v-if="monthlyComparison" class="summary-change-inline">
                            <span>({{ selectedPeriod === "monthly" ? "전월" : "전년" }}대비 </span>
                            <span
                                v-if="monthlyComparison.hasPreviousAmount"
                                :class="[
                                    'summary-change-value',
                                    monthlyComparison.amountChange >= 0 ? 'positive' : 'negative',
                                ]"
                            >
                                {{ monthlyComparison.amountChange >= 0 ? "↑" : "↓"
                                }}{{ Math.abs(monthlyComparison.amountChangePercent).toFixed(1) }}%
                            </span>
                            <span v-else class="summary-change-value no-data">-</span>
                            <span>)</span>
                        </span>
                    </div>
                </div>
                <div class="summary-card">
                    <i class="fas fa-bolt summary-card-icon"></i>
                    <div class="summary-label">총 사용량</div>
                    <div class="summary-value-container">
                        <div class="summary-value">{{ formatEnergyUsage(dashboardData?.totals?.totalUsage) }}</div>

                        <span v-if="monthlyComparison" class="summary-change-inline">
                            <span>({{ selectedPeriod === "monthly" ? "전월" : "전년" }}대비 </span>
                            <span
                                v-if="monthlyComparison.hasPreviousUsage"
                                :class="[
                                    'summary-change-value',
                                    monthlyComparison.usageChange >= 0 ? 'positive' : 'negative',
                                ]"
                            >
                                {{ monthlyComparison.usageChange >= 0 ? "↑" : "↓"
                                }}{{ Math.abs(monthlyComparison.usageChangePercent).toFixed(1) }}%
                            </span>
                            <span v-else class="summary-change-value no-data">-</span>
                            <span>)</span>
                        </span>
                    </div>
                </div>
                <!-- <div class="summary-card">
                    <div class="summary-label">고지서 수</div>
                    <div class="summary-value">{{ formatNumber(dashboardData?.totals?.customerCount) }}개</div>
                </div> -->
                <div class="summary-card">
                    <i class="fas fa-calculator summary-card-icon"></i>
                    <div class="summary-label">평균 단위 금액</div>
                    <div class="summary-value-container">
                        <div class="summary-value">
                            {{
                                dashboardData?.totals?.avgUnitPrice ? dashboardData.totals.avgUnitPrice.toFixed(1) : "-"
                            }}
                            원/kWh
                        </div>
                    </div>
                </div>
                <div class="summary-decoration">
                    <div class="decoration-content">
                        <div class="decoration-lines">
                            <div class="decoration-line"></div>
                            <div class="decoration-line"></div>
                            <div class="decoration-line"></div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 중간: 좌우 레이아웃 -->
            <div class="main-content-section">
                <!-- 좌측: 간소화된 고지서 목록 -->
                <div class="bill-list-compact-section">
                    <DashboardBillListCompact
                        :billList="filteredAndSortedBillList"
                        @select-customer="handleCustomerSelect"
                        @customers-updated="handleCustomersUpdated"
                    />
                </div>

                <!-- 우측: 차트 영역 -->
                <div class="charts-section">
                    <div class="chart-item" v-if="dashboardData && dashboardData.billList">
                        <h3 class="chart-title">
                            <!-- <i class="fas fa-th-large"></i> -->
                            고지서별 청구 금액 분포
                        </h3>
                        <DashboardBillPieChart
                            ref="pieChart"
                            :data="dashboardData.billList"
                            @select-customer="handleCustomerSelect"
                            :dashboardData="dashboardData"
                            class="chart-wrapper"
                        />
                    </div>
                    <div class="chart-item" v-if="dashboardData && dashboardData.billTrend">
                        <h3 class="chart-title">
                            <!-- <i class="fas fa-chart-line"></i> -->
                            청구 요금 추이
                        </h3>
                        <DashboardBillTrendChart
                            ref="billTrendChart"
                            :data="dashboardData.billTrend.data"
                            :customers="dashboardData.billTrend.customers"
                            :selectedPeriod="selectedPeriod"
                            class="chart-wrapper"
                        />
                    </div>
                    <div class="chart-item" v-if="dashboardData && dashboardData.usageTrend">
                        <h3 class="chart-title">
                            <!-- <i class="fas fa-chart-bar"></i> -->
                            {{ selectedPeriod === "monthly" ? "일별" : "월별" }} 사용량 추이
                        </h3>
                        <DashboardUsageTrendChart
                            ref="usageTrendChart"
                            :data="dashboardData.usageTrend.data"
                            :customers="dashboardData.usageTrend.customers"
                            :selectedPeriod="selectedPeriod"
                            class="chart-wrapper"
                        />
                    </div>
                    <!-- 4번째 칸: 요금 통계 및 인사이트 -->
                    <div class="chart-item insights-card">
                        <DashboardInsights
                            :selected-period="selectedPeriod"
                            :trend-stats="trendStats"
                            :bill-composition="billComposition"
                            :dashboard-data="dashboardData"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import DashboardBillPieChart from "./DashboardBillPieChart.vue";
import DashboardBillTrendChart from "./DashboardBillTrendChart.vue";
import DashboardUsageTrendChart from "./DashboardUsageTrendChart.vue";
import DashboardBillListCompact from "./DashboardBillListCompact.vue";
import DashboardInsights from "./DashboardInsights.vue";
import backEndApi from "@api/backEndApi.js";
import html2pdf from "html2pdf.js";
import { formatSignificantTwoDigitsGrouped } from "@src/utils/currencyUtils.js";
import { formatEnergyUsage as formatEnergyUsageUtil } from "../../utils/energyUtils.js";

export default {
    name: "DashboardMain",
    components: {
        DashboardBillPieChart,
        DashboardBillTrendChart,
        DashboardUsageTrendChart,
        DashboardBillListCompact,
        DashboardInsights,
    },
    props: {
        selectedPeriod: {
            type: String,
            default: "monthly",
        },
        currentYear: {
            type: Number,
            default: () => new Date().getFullYear(),
        },
        currentMonth: {
            type: Number,
            default: () => new Date().getMonth() - 1,
        },
        dataStatus: {
            type: String,
            default: "active",
        },
    },
    data() {
        return {
            dashboardData: null,
            previousYear: new Date().getFullYear(),
            previousMonth: new Date().getMonth() - 1,
            resizeObserver: null,
            isLoading: false,
            isInitialLoad: true,
        };
    },
    computed: {
        currentDateDisplay() {
            return `${this.currentYear}년 ${this.currentMonth + 1}월`;
        },
        currentYearDisplay() {
            return `${this.currentYear}년`;
        },
        yearRange() {
            const currentYear = new Date().getFullYear();
            const years = [];
            for (let i = currentYear - 5; i <= currentYear + 5; i++) {
                years.push(i);
            }
            return years;
        },
        isNavigationDisabled() {
            return false;
        },
        regDt() {
            return this.getDate(this.selectedPeriod);
        },
        filteredAndSortedBillList() {
            if (!this.dashboardData || !this.dashboardData.billList) {
                return [];
            }
            // 정렬: 총 청구금액 내림차순
            return [...this.dashboardData.billList].sort((a, b) => {
                return (b.totalAmount || 0) - (a.totalAmount || 0);
            });
        },
        billComposition() {
            if (!this.dashboardData || !this.dashboardData.billList || this.dashboardData.billList.length === 0) {
                return {
                    basePercent: 0,
                    kwhPercent: 0,
                    otherPercent: 0,
                    taxPercent: 0,
                    totalBase: 0,
                    totalKwh: 0,
                    totalOther: 0,
                    totalTax: 0,
                    totalAmount: 0,
                };
            }

            const bills = this.dashboardData.billList;
            let totalBase = 0;
            let totalKwh = 0;
            let totalOther = 0;
            let totalTax = 0;
            let totalAmount = 0;

            bills.forEach((bill) => {
                const baseBill = bill.billDetails?.baseBill || 0;
                const kwhBill = bill.billDetails?.kwhBill || 0;
                const reqBill = bill.billDetails?.reqBill || 0;
                const discountBill = bill.billDetails?.discountBill || 0;
                const billTotalAmount = bill.totalAmount || 0;

                // 기타요금 = 청구전력요금 - 기본요금 - 전력량요금
                const otherBill = Math.max(0, reqBill - baseBill - kwhBill);
                // 부가세 및 전력기금 = 총 청구금액 - 청구전력요금 + 할인요금
                const taxAndFund = Math.max(0, billTotalAmount - reqBill + discountBill);

                totalBase += baseBill;
                totalKwh += kwhBill;
                totalOther += otherBill;
                totalTax += taxAndFund;
                totalAmount += billTotalAmount;
            });

            if (totalAmount === 0) {
                return {
                    basePercent: 0,
                    kwhPercent: 0,
                    otherPercent: 0,
                    taxPercent: 0,
                    totalBase: 0,
                    totalKwh: 0,
                    totalOther: 0,
                    totalTax: 0,
                    totalAmount: 0,
                };
            }

            return {
                basePercent: (totalBase / totalAmount) * 100,
                kwhPercent: (totalKwh / totalAmount) * 100,
                otherPercent: (totalOther / totalAmount) * 100,
                taxPercent: (totalTax / totalAmount) * 100,
                totalBase,
                totalKwh,
                totalOther,
                totalTax,
                totalAmount,
            };
        },
        trendStats() {
            if (
                !this.dashboardData ||
                !this.dashboardData.billTrend ||
                !this.dashboardData.billTrend.data ||
                this.dashboardData.billTrend.data.length === 0
            ) {
                return null;
            }

            const trendData = this.dashboardData.billTrend.data;
            // 0이 아닌 값만 필터링
            const validAmounts = trendData.map((item) => item.totalReqAmt || 0).filter((amt) => amt > 0);

            if (validAmounts.length === 0) {
                return null;
            }

            return {
                avgAmount: validAmounts.reduce((a, b) => a + b, 0) / validAmounts.length,
                maxAmount: Math.max(...validAmounts),
                minAmount: Math.min(...validAmounts),
            };
        },
        monthlyComparison() {
            if (
                !this.dashboardData ||
                !this.dashboardData.billTrend ||
                !this.dashboardData.billTrend.data ||
                this.dashboardData.billTrend.data.length < 2
            ) {
                return null;
            }

            const trendData = this.dashboardData.billTrend.data;
            const current = trendData[trendData.length - 1];
            const previous = trendData[trendData.length - 2];

            if (!current || !previous) return null;

            const currentAmount = current.totalReqAmt || 0;
            const previousAmount = previous.totalReqAmt || 0;
            const currentUsage = current.totalUsage || 0;
            const previousUsage = previous.totalUsage || 0;

            // 전년(월) 데이터가 없는 경우 비교 불가
            const hasPreviousAmount = previousAmount > 0;
            const hasPreviousUsage = previousUsage > 0;

            return {
                amountChange: currentAmount - previousAmount,
                amountChangePercent: hasPreviousAmount ? ((currentAmount - previousAmount) / previousAmount) * 100 : 0,
                usageChange: currentUsage - previousUsage,
                usageChangePercent: hasPreviousUsage ? ((currentUsage - previousUsage) / previousUsage) * 100 : 0,
                hasPreviousAmount,
                hasPreviousUsage,
            };
        },
    },
    watch: {
        regDt(newVal, oldVal) {
            if (oldVal !== undefined && newVal !== oldVal) {
                console.log("데이터 조회 날짜가 변경되었습니다. ", newVal);
                this.getDashboardData(newVal);
            }
        },
    },
    mounted() {
        this.initData();
        this.$nextTick(() => {
            this.initResizeObserver();
        });
        window.addEventListener("resize", this.handleWindowResize);
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.handleWindowResize);
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },
    methods: {
        formatNumber(value) {
            const num = Number(value);
            return isNaN(num) ? "" : num.toLocaleString();
        },
        formatCurrency(value) {
            const num = Number(value);
            if (isNaN(num) || num === 0) return "-";
            return formatSignificantTwoDigitsGrouped(num);
        },
        formatEnergyUsage(value) {
            return formatEnergyUsageUtil(value);
        },
        async getDashboardData(regDt) {
            this.isLoading = true;
            this.$emit("update-loading-state", true);
            try {
                let { data, status } = await backEndApi.kepco.analyzeAllUsageCost(regDt);
                if (status !== 200) {
                    throw new Error(data.message);
                }
                this.dashboardData = data.data;

                // customerList 업데이트
                if (this.dashboardData && this.dashboardData.billList) {
                    const list = this.dashboardData.billList.map((bill) => ({
                        custNo: bill.custNo,
                        custNm: bill.custNm,
                    }));
                    this.$emit("update-customer-list", list);
                } else {
                    this.$emit("update-customer-list", []);
                }

                // 데이터 상태 및 메시지 업데이트
                this.$emit("update-data-status", data.data?.status || "active");
                this.$emit("update-status-message", data.data?.message || "");
                this.$emit("update-loading-state", false);
            } catch (error) {
                console.error("대시보드 데이터 로드 오류:", error);
                if (this.alertWarning) {
                    this.alertWarning(`${error}`);
                } else if (this.alertNoti) {
                    this.alertNoti("데이터를 불러오는 중 오류가 발생했습니다.", error.message || String(error));
                }
                this.rollbackDatePicker();
                this.$emit("update-customer-list", []);
            } finally {
                this.isLoading = false;
                this.$emit("update-loading-state", false);
                if (this.isInitialLoad) {
                    this.isInitialLoad = false;
                }
            }
        },
        getDate(type) {
            const year = this.currentYear;
            const month = String(this.currentMonth + 1).padStart(2, "0");
            if (type === "monthly") {
                return `${year}${month}`;
            } else if (type === "yearly") {
                return `${year}`;
            }
        },
        async initData() {
            const regDt = this.getDate(this.selectedPeriod);
            await this.getDashboardData(regDt);
        },
        saveCurrentDate() {
            this.previousYear = this.currentYear;
            this.previousMonth = this.currentMonth;
        },
        rollbackDatePicker() {
            this.$emit("rollback-date", {
                year: this.previousYear,
                month: this.previousMonth,
            });
        },
        exportPDF() {
            const element = this.$refs.costAnalysisDashboard;
            if (!element) return;

            const date = this.selectedPeriod === "monthly" ? this.currentDateDisplay : this.currentYearDisplay;

            const opt = {
                margin: 10,
                filename: `전기요금 대시보드_${date}.pdf`,
                image: { type: "jpeg", quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { unit: "mm", format: [500, 297], orientation: "landscape" },
            };

            html2pdf().set(opt).from(element).save();
        },
        handleCustomerSelect(custNo) {
            this.$emit("select-customer", custNo);
        },
        async handleCustomersUpdated() {
            // 고객 관리에서 데이터 변경 시 대시보드 데이터 재조회
            const regDt = this.getDate(this.selectedPeriod);
            await this.getDashboardData(regDt);
        },
        initResizeObserver() {
            if (typeof ResizeObserver === "undefined") {
                return;
            }

            const chartsSection = this.$el?.querySelector(".charts-section");
            if (!chartsSection) {
                return;
            }

            this.resizeObserver = new ResizeObserver(() => {
                this.$nextTick(() => {
                    this.resizeAllCharts();
                });
            });

            // 차트 섹션과 각 차트 아이템 관찰
            this.resizeObserver.observe(chartsSection);

            // 각 차트 아이템도 개별적으로 관찰
            const chartItems = chartsSection.querySelectorAll(".chart-item");
            chartItems.forEach((item) => {
                this.resizeObserver.observe(item);
            });
        },
        handleWindowResize() {
            this.$nextTick(() => {
                this.resizeAllCharts();
            });
        },
        resizeAllCharts() {
            this.$nextTick(() => {
                // Pie 차트 리사이즈
                if (this.$refs.pieChart && this.$refs.pieChart.chart) {
                    this.$refs.pieChart.chart.resize();
                }
                // BillTrend 차트 리사이즈
                if (this.$refs.billTrendChart && this.$refs.billTrendChart.chart) {
                    this.$refs.billTrendChart.chart.resize();
                }
                // UsageTrend 차트 리사이즈
                if (this.$refs.usageTrendChart && this.$refs.usageTrendChart.chart) {
                    this.$refs.usageTrendChart.chart.resize();
                }
            });
        },
    },
};
</script>

<style scoped>
.summary-value-container {
    display: flex;
    gap: 4px;
    align-items: center;
    justify-content: flex-end;
    position: relative;
    z-index: 1;
}
.dashboard-container {
    color: #1e293b;
    padding: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #ffffff;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;
    margin-top: 8px;
    position: relative;
}

.dashboard-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px;
    /* overflow: hidden; */
    min-height: 0;
}

/* 요약 섹션 */
.summary-section {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 12px;
    flex-shrink: 0;
}

.summary-card {
    background: linear-gradient(135deg, #607d8b 0%, #455a64 100%);
    border-radius: 16px;
    padding: 20px 24px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 110px;
    position: relative;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 20px rgba(84, 110, 122, 0.3);
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.18);
}

.summary-card::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    background: radial-gradient(circle at top right, rgba(255, 255, 255, 0.15) 0%, transparent 60%);
    opacity: 0;
    transition: opacity 0.4s ease;
}

.summary-card::after {
    content: "";
    position: absolute;
    bottom: -2px;
    left: -2px;
    right: -2px;
    height: 3px;
    /* background: linear-gradient(90deg, #4fc3f7, #29b6f6, #4fc3f7); */
    opacity: 0;
    transition: opacity 0.4s ease;
    border-radius: 0 0 16px 16px;
}

.summary-card:hover {
    transform: translateY(-6px) scale(1.02);
    box-shadow: 0 12px 40px rgba(84, 110, 122, 0.45);
}

.summary-card:hover::before {
    opacity: 1;
}

.summary-card:hover::after {
    opacity: 1;
}

.summary-card-primary {
    background: linear-gradient(135deg, #546e7a 0%, #37474f 100%);
    box-shadow: 0 6px 24px rgba(84, 110, 122, 0.4);
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.summary-card-primary::before {
    background: repeating-linear-gradient(
        45deg,
        transparent,
        transparent 20px,
        rgba(255, 255, 255, 0.02) 20px,
        rgba(255, 255, 255, 0.02) 40px
    );
    opacity: 1;
}

.summary-card-primary:hover {
    box-shadow: 0 16px 48px rgba(84, 110, 122, 0.6);
}

.summary-card-primary .summary-card-icon {
    opacity: 0.2;
    font-size: 120px;
}

.summary-card-primary:hover .summary-card-icon {
    opacity: 0.3;
}

.summary-card-primary .summary-label {
    color: rgba(255, 255, 255, 0.95);
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.summary-card-primary .summary-value {
    color: #ffffff;
    font-size: 28px;
    font-weight: 900;
    text-shadow: 0 3px 12px rgba(0, 0, 0, 0.3);
    letter-spacing: -1px;
}

.summary-card-icon {
    position: absolute;
    right: -15px;
    top: 50%;
    transform: translateY(-50%) rotate(-12deg);
    font-size: 110px;
    opacity: 0.15;
    color: #ffffff;
    z-index: 0;
    pointer-events: none;
    line-height: 1;
    transition: all 0.4s ease;
}

.summary-card:hover .summary-card-icon {
    opacity: 0.25;
    transform: translateY(-50%) rotate(-8deg) scale(1.1);
}

.summary-card-primary .summary-card-icon {
    opacity: 0.2;
}

.summary-card-primary:hover .summary-card-icon {
    opacity: 0.3;
}

.summary-label {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.85);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    align-self: flex-start;
    margin-bottom: 12px;
    position: relative;
    z-index: 1;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.summary-card-primary .summary-label {
    color: rgba(255, 255, 255, 0.95);
    font-weight: 700;
    letter-spacing: 1.2px;
}

.summary-value {
    font-size: 24px;
    color: #ffffff;
    font-weight: 800;
    align-self: flex-end;
    margin-top: auto;
    line-height: 1.3;
    position: relative;
    z-index: 1;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    letter-spacing: -0.5px;
}

.summary-card-primary .summary-value {
    color: #ffffff;
    font-size: 24px;
    font-weight: 900;
    text-shadow: 0 3px 12px rgba(0, 0, 0, 0.25);
}

.summary-change-inline {
    font-size: 11px;
    font-weight: 500;
    margin-left: 4px;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.summary-change-inline > span {
    color: rgba(255, 255, 255, 0.9);
    font-weight: 600;
}

.summary-change-value {
    font-size: 12px;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 6px;
    display: inline-block;
}

.summary-change-value.positive {
    color: #fee2e2;
    background: rgba(220, 38, 38, 0.25);
}

.summary-change-value.negative {
    color: #d1fae5;
    background: rgba(5, 150, 105, 0.25);
}

.summary-change-value.no-data {
    color: rgba(255, 255, 255, 0.6);
    font-weight: 500;
    background: rgba(255, 255, 255, 0.1);
}

.summary-sub-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-top: 4px;
    font-size: 10px;
    color: #64748b;
}

.summary-sub-info span {
    font-weight: 500;
}

/* Summary 장식 영역 */
.summary-decoration {
    grid-column: span 2;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px 24px;
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    border-radius: 16px;
    position: relative;
    overflow: hidden;
    min-height: 110px;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 20px rgba(148, 163, 184, 0.15);
    border: 1px solid rgba(148, 163, 184, 0.2);
}

.summary-decoration::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: repeating-linear-gradient(
        45deg,
        transparent,
        transparent 15px,
        rgba(203, 213, 225, 0.15) 15px,
        rgba(203, 213, 225, 0.15) 30px
    );
    opacity: 0.5;
}

.summary-decoration:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(148, 163, 184, 0.25);
}

.decoration-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    position: relative;
    z-index: 1;
}

.decoration-icon {
    width: 40px;
    height: 40px;
    color: #94a3b8;
    opacity: 0.5;
}

.decoration-icon svg {
    width: 100%;
    height: 100%;
}

.decoration-lines {
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
    max-width: 80px;
}

.decoration-line {
    height: 3px;
    background: linear-gradient(90deg, transparent, #cbd5e1, transparent);
    border-radius: 2px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.decoration-line:nth-child(1) {
    width: 100%;
}

.decoration-line:nth-child(2) {
    width: 75%;
    margin: 0 auto;
}

.decoration-line:nth-child(3) {
    width: 50%;
    margin: 0 auto;
}

/* 메인 콘텐츠 섹션 */
.main-content-section {
    display: grid;
    grid-template-columns: 27% 1fr;
    gap: 12px;
    flex: 1;
    min-height: 0;
    min-width: 0;
    overflow: hidden;
}

/* 좌측: 간소화된 고지서 목록 */
.bill-list-compact-section {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    overflow-y: auto;
    overflow-x: visible;
}

/* 우측: 차트 섹션 */
.charts-section {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-auto-rows: 1fr;
    gap: 12px;
    min-height: 0;
    overflow: hidden;
}

.chart-item {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0;
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
}

.chart-wrapper {
    width: 100%;
    flex: 1;
    min-height: 0;
}

.chart-title {
    font-size: 15px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
    padding: 12px 12px 8px 12px;
    /* border-bottom: 2px solid #3b82f6; */
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 6px;
}

.chart-title i {
    color: #3b82f6;
    font-size: 11px;
}

/* 인사이트 카드 */
.insights-card {
    display: flex;
    flex-direction: column;
}

/* 데이터 없음 placeholder */
.data-empty-placeholder {
    height: 100%;
    background: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
}

.empty-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 40px;
}

.empty-icon {
    width: 80px;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    border-radius: 50%;
    color: #94a3b8;
    font-size: 36px;
}

.empty-text {
    font-size: 18px;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: 0.3px;
}

.empty-subtext {
    font-size: 13px;
    font-weight: 500;
    color: #64748b;
    text-align: center;
    line-height: 1.5;
}

/* 로딩 오버레이 */
.loading-overlay {
    height: 100%;
    width: 100%;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    /* z-index: 1000; */
    border-radius: 8px;
    animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

.loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    padding: 40px;
}

.loading-spinner {
    position: relative;
    width: 80px;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.spinner-ring {
    position: absolute;
    width: 100%;
    height: 100%;
    border: 4px solid transparent;
    border-top-color: #607d8b;
    border-radius: 50%;
    animation: spin 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}

.spinner-ring:nth-child(1) {
    animation-delay: -0.45s;
    border-top-color: #546e7a;
}

.spinner-ring:nth-child(2) {
    animation-delay: -0.3s;
    border-top-color: #607d8b;
    width: 70%;
    height: 70%;
}

.spinner-ring:nth-child(3) {
    animation-delay: -0.15s;
    border-top-color: #78909c;
    width: 50%;
    height: 50%;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

.loading-text {
    font-size: 18px;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: 0.3px;
    animation: pulse 1.5s ease-in-out infinite;
}

.loading-subtext {
    font-size: 13px;
    font-weight: 500;
    color: #64748b;
    text-align: center;
}

@keyframes pulse {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.6;
    }
}

/* 콘텐츠 로딩 상태 */
.dashboard-content.loading-state {
    position: relative;
    opacity: 0.6;
    pointer-events: none;
}

.dashboard-content.loading-state * {
    transition: opacity 0.3s ease;
}

@keyframes fadeInSlide {
    from {
        transform: translateY(-8px);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

.loading-indicator-text {
    font-size: 12px;
    font-weight: 500;
    color: #ffffff;
    letter-spacing: 0.3px;
    white-space: nowrap;
}
</style>
